<template>
    <Dialog :open="true" @update:open="$emit('close')">
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>{{ title }}</DialogTitle>
            </DialogHeader>

            <div class="space-y-4">
                <p class="text-sm text-muted-foreground">
                    <span class="font-medium text-foreground">Two-Factor Authentication is now enabled.</span>
                    Store these recovery codes in a secure password manager.
                    They can be used to recover access to your account when you
                    are not able to authenticate using your device.
                </p>

                <div class="rounded-md border bg-new-neutral-50 p-4 font-mono text-sm leading-7">
                    <p v-for="code in recoveryCodes" :key="code">
                        {{ code }}
                    </p>
                </div>

                <div class="flex gap-3">
                    <Button variant="outline" size="sm" @click="copyToClipboard">
                        <Copy class="mr-2 size-4" />
                        {{ copied ? 'Copied!' : 'Copy to clipboard' }}
                    </Button>
                    <Button v-if="canRegenerate" variant="outline" size="sm" @click="regenerateCodes">
                        <RefreshCw class="mr-2 size-4" />
                        Regenerate codes
                    </Button>
                </div>
            </div>

            <DialogFooter class="mt-4">
                <Button size="lg" @click="$emit('close')">Done</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { useClipboard } from '@vueuse/core';
import { toast } from 'vue-sonner';
import { Copy, RefreshCw } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const props = defineProps<{
    title?: string;
    recoveryCodes: string[];
    canRegenerate?: boolean;
}>();

defineEmits<{
    close: [];
}>();

const { copy } = useClipboard();
const copied = ref(false);

const copyToClipboard = () => {
    copy(props.recoveryCodes.join('\n'));
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 2000);
};

const regenerateCodes = async () => {
    try {
        await axios.post(route('auth.regenerate-recovery-codes'));
        toast.success('Recovery codes regenerated');
        window.location.reload();
    } catch {
        toast.error('Failed to regenerate codes');
    }
};
</script>
